import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { IconExViewGrid, IconExViewList } from '@/components/ui/icons'
import { cn } from '@/lib/utils'
import { useLocalize } from '@/packages/explore/lib/localize'
import type { ExploreFilter, ExploreLayout } from '@/packages/explore/types'

interface ExploreFilterRowProps {
  filters: ExploreFilter[]
  selected: string | null
  onSelect: (id: string | null) => void
  layout: ExploreLayout
  onLayoutChange: (layout: ExploreLayout) => void
  nodeId?: string
}

/**
 * Figma `panel-list` › `filter` (2267:290723) — the chip strip and the
 * grid/list toggle.
 *
 * The strip scrolls rather than wrapping; Figma masks its right edge with a
 * 16px fade, which is the gradient on the wrapper here. Chips are 28px tall on
 * the 700px panel and 44px below 900px, following the app-wide button sizes.
 */
export function ExploreFilterRow({
  filters,
  selected,
  onSelect,
  layout,
  onLayoutChange,
  nodeId,
}: ExploreFilterRowProps): ReactElement {
  const { t } = useTranslation()
  const localize = useLocalize()

  const views: { id: ExploreLayout; label: string; Icon: typeof IconExViewGrid }[] = [
    { id: 'grid', label: t('explore.viewGrid'), Icon: IconExViewGrid },
    { id: 'list', label: t('explore.viewList'), Icon: IconExViewList },
  ]

  return (
    <div data-node-id={nodeId} data-name="panel-list" className="hub-rule w-full pb-3">
      <div data-name="filter" className="flex w-full items-start gap-2">
        <div className="relative flex min-w-px flex-1 items-center">
          <div
            data-name="chips"
            className="hub-scroll-x flex w-full items-center gap-2 overflow-x-auto"
          >
            {filters.map((filter) => {
              const active = filter.id === selected
              return (
                <button
                  key={filter.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => {
                    onSelect(active ? null : filter.id)
                  }}
                  className={cn(
                    'hub-label-md shrink-0 cursor-pointer rounded-full px-4 py-2 whitespace-nowrap transition-colors hover:brightness-95 md:py-1 dark:hover:brightness-85',
                    active
                      ? 'bg-btn-primary text-btn-text-inverse hub-label-md-highlight'
                      : 'bg-btn-secondary-raised text-btn-text hub-stroke',
                  )}
                >
                  {localize(filter.label)}
                </button>
              )
            })}
          </div>

          {/* Figma `Rectangle 6188` — 16px of the panel background over the
              right edge of the strip, so a cut-off chip fades out. */}
          <div
            aria-hidden="true"
            className="from-cookies-surface pointer-events-none absolute inset-y-0 right-0 w-4 bg-linear-to-l to-transparent"
          />
        </div>

        <div
          data-name="tab"
          role="group"
          aria-label={t('explore.layout')}
          className="bg-btn-secondary-raised hub-stroke flex shrink-0 items-center rounded-full p-1"
        >
          {views.map(({ id, label, Icon }) => {
            const active = id === layout
            return (
              <button
                key={id}
                type="button"
                aria-pressed={active}
                aria-label={label}
                onClick={() => {
                  onLayoutChange(id)
                }}
                className={cn(
                  'hover:bg-btn-secondary-base flex cursor-pointer items-center justify-center rounded-full px-2 py-1 transition-colors',
                  active ? 'bg-btn-secondary-base hub-stroke' : 'text-widget-secondary',
                  'md:py-0.5',
                )}
              >
                <Icon className="size-5 shrink-0 md:size-4" aria-hidden="true" />
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
